import { createElement } from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

import { BreadcrumbStrip, type TrailCrumb } from "./breadcrumb-strip"
import { FlashSlot } from "./flash-slot"

export type { Crumb, FoldedCrumb, TrailCrumb } from "./breadcrumb-strip"
export { useTrail, useWorkspaceCrumb } from "./crumbs"

// One control at most per slot: the page's single primary action, and an
// optional secondary before it. Anything else a page offers lives in its
// content. Pass an ActionButton or ActionLink (components/shared/
// action-buttons), which the row collapses to an icon-only round control
// below `sm`, keeping its accessible name.
export type PageChromeControl = React.ReactElement | false | null | undefined

// The record a page is about. Nothing in it is interactive.
export type PageIdentity = {
  name: string
  // One short state of the record, as a chip beside the name.
  status?: string
  // The record's own facts, read as one line beneath the name.
  facts?: string[]
}

export type PageChromeProps = {
  // The current page's icon and name: the trail's last crumb.
  icon: LucideIcon
  title: string
  // The id a form points its `aria-labelledby` at. It sits on the level-one
  // heading: the identity's name when there is one, else the last crumb.
  titleId?: string
  // The ancestors only, root first; the current page is the title. A root
  // page declares none and is its own only crumb, with no Back. A long trail
  // may fold its older ancestors into one "…" crumb.
  crumbs?: TrailCrumb[]
  // The record the page is about, named beneath the crumb row. A page with
  // nothing to name, or whose record is not saved yet, passes none.
  identity?: PageIdentity
  // An outline control before the primary action.
  secondary?: PageChromeControl
  // The page's one solid primary action, at the row's end.
  action?: PageChromeControl
  className?: string
}

// Below `sm` a control in the row keeps its icon and hides its label from
// sight only, so its accessible name is unchanged, and turns round.
const collapseControls = [
  "max-sm:[&_[data-slot=action-label]]:sr-only",
  "max-sm:[&_[data-slot=action]]:size-9",
  "max-sm:[&_[data-slot=action]]:px-0",
]

// The chrome every page renders. The crumb row sits on a band that spans
// the viewport, one step deeper than the ground so it reads apart from the
// nav header: Back, the trail from the root to the parent, the current page
// as its last crumb with its icon, then the secondary and the primary action.
// The band is 56px on every page, a root's included. Beneath it, in the
// page's gutter, the flash slot and then, on a page about one named record,
// the identity row. It sits in normal flow above the page body and scrolls
// with it.
export function PageChrome({
  icon,
  title,
  titleId,
  crumbs = [],
  identity,
  secondary,
  action,
  className,
}: PageChromeProps) {
  return (
    <div
      data-testid="page-chrome"
      className={cn("flex w-full flex-col", className)}
    >
      <div data-testid="page-chrome-band" className="bg-brand-background-deep">
        <div className="container mx-auto flex min-h-14 items-center gap-3 px-4 py-2">
          <BreadcrumbStrip
            crumbs={crumbs}
            current={{
              icon,
              label: title,
              heading: identity === undefined,
              titleId,
            }}
          />
          {(secondary || action) && (
            <div
              data-testid="page-chrome-actions"
              className={cn(
                "flex shrink-0 items-center gap-2 empty:hidden",
                collapseControls,
              )}
            >
              {secondary}
              {action}
            </div>
          )}
        </div>
      </div>
      <FlashSlot />
      {identity !== undefined && (
        <IdentityRow icon={icon} identity={identity} titleId={titleId} />
      )}
    </div>
  )
}

function IdentityRow({
  icon,
  identity: { name, status, facts = [] },
  titleId,
}: {
  icon: LucideIcon
  identity: PageIdentity
  titleId?: string
}) {
  return (
    <div
      data-testid="page-chrome-identity"
      className="container mx-auto flex flex-col gap-1 px-4 pt-4"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="flex min-w-0 items-start gap-2 text-2xl leading-tight font-bold tracking-tight text-black">
          {createElement(icon, { className: "mt-[0.2em] size-6 shrink-0" })}
          <span id={titleId} className="min-w-0 break-words">
            {name}
          </span>
        </h1>
        {status !== undefined && (
          <span
            data-testid="page-chrome-status"
            className="bg-brand-light-philo-mustard font-control inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs text-black"
          >
            {status}
          </span>
        )}
      </div>
      {facts.length > 0 && (
        <p
          data-testid="page-chrome-facts"
          className="text-foreground/70 flex flex-wrap gap-x-1.5 pl-8 text-sm"
        >
          {facts.map((fact, index) => (
            <span key={fact} className="inline-flex gap-x-1.5">
              {index > 0 && <span aria-hidden>·</span>}
              {fact}
            </span>
          ))}
        </p>
      )}
    </div>
  )
}
